import React, { useState } from 'react';
import PropTypes from 'prop-types';
import DocumentTitle from 'react-document-title';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import axios from 'axios';
import Select from 'react-select';
import _ from 'lodash';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Routes from 'utils/routes.js.erb';
import { authHeaders } from 'actions';
import { errorMessage } from 'actions/internal';
import TopNavBar from 'containers/nav/TopNavBar';
import SubNavBar from 'containers/nav/SubNavBar';
import AdministrationSubNav from 'components/administration/AdministrationSubNav';
import { LoadingSpinner } from 'components/helpers/LoadingSpinner';
import { formatDate, getTimeZone } from 'utils/Formatters';
import ReportForm from './ReportForm';
import ReportHistory from './ReportHistory';

const POLL_INTERVAL = 3000;
const ACTIVE_STATUSES = ['pending', 'in_progress'];

const AdministrationReportingPage = ({ onErrorMessage }) => {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedKey, setSelectedKey] = useState('');
  const [pendingKey, setPendingKey] = useState(null);
  // Reports read their dates and print their times in the viewer's zone, so
  // the file matches the days and hours the rest of the dashboard shows.
  const timeZone = getTimeZone();

  const reportsQuery = useQuery({
    // Report names and descriptions come back in the dashboard language, so
    // switching it refetches the catalog; the previous one stays on screen
    // meanwhile instead of the spinner.
    queryKey: ['administration', 'reports', i18n.language],
    placeholderData: keepPreviousData,
    queryFn: ({ signal }) => axios.get(
      Routes.api_dashboard_administration_reports_path(),
      { headers: authHeaders(), signal }
    ).then(response => response.data),
    // Polls only while a report is still running, so the page goes quiet once
    // everything has finished.
    refetchInterval: (query) => {
      const history = _.get(query, 'state.data.history', []);
      return history.some(report => ACTIVE_STATUSES.includes(report.status)) ? POLL_INTERVAL : false;
    },
    // A rejected request is an answer, not a blip: retrying it would keep the
    // page on the spinner for several seconds before saying so.
    retry: (failureCount, error) => _.get(error, 'response.status') !== 403 && failureCount < 2,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['administration', 'reports'] });

  const onError = (error) => {
    onErrorMessage(_.get(error, 'response.data.error') || t('administration.reporting.error'));
  };

  const generateMutation = useMutation({
    mutationFn: ({ reportKey, reportParams }) => axios.post(
      Routes.api_dashboard_administration_reports_path(),
      { report_key: reportKey, report_params: { ...reportParams, time_zone: timeZone } },
      { headers: authHeaders() }
    ).then(response => response.data),
    onMutate: ({ reportKey }) => setPendingKey(reportKey),
    onSettled: () => setPendingKey(null),
    onSuccess: invalidate,
    onError,
  });

  const cancelMutation = useMutation({
    mutationFn: (id) => axios.post(
      Routes.cancel_api_dashboard_administration_report_path({ id }),
      {},
      { headers: authHeaders() }
    ),
    onSuccess: invalidate,
    onError,
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => axios.delete(
      Routes.api_dashboard_administration_report_path({ id }),
      { headers: authHeaders() }
    ),
    onSuccess: invalidate,
    onError,
  });

  const reports = _.get(reportsQuery, 'data.reports', []);
  const selectedReport = reports.find(report => report.key === selectedKey);

  const options = reports.map(report => ({ value: report.key, label: report.name, report }));
  const selectedOption = options.find(option => option.value === selectedKey) || null;

  // react-select renders real markup per option, so the description sits on its
  // own line under the name in the menu. `isSelected` keeps the closed control
  // showing just the name.
  const formatOptionLabel = (option, { context }) => {
    if (context === 'value' || !option.report.description) {
      return option.label;
    }

    return (
      <div>
        <div className='o-report-option__name'>{option.label}</div>
        <div className='o-report-option__description'>{option.report.description}</div>
      </div>
    );
  };

  const renderBody = () => {
    if (reportsQuery.isPending) {
      return <LoadingSpinner bgColor='light' />;
    }

    // The route is open to every dashboard admin while the API is restricted to
    // Max Permission users, so a denied request has to say so rather than leave
    // the page spinning.
    if (reportsQuery.isError) {
      return (
        <div className='o-card__wrap o-admin-card u-margin-top--base'>
          <p>{t('administration.reporting.loadError')}</p>
        </div>
      );
    }

    return (
      <>
        <div className='o-card__wrap o-admin-card u-margin-top--base'>
          <h3><b>{t('administration.reporting.title')}</b></h3>
          <p className='u-txt--label'>{t('administration.reporting.intro')}</p>

          <div className='u-margin-top--base'>
            <span className='u-txt--label'>{t('administration.reporting.selectReport')}</span>
            <Select
              name='sp-report-select'
              className='o-select'
              classNamePrefix='o-report-select'
              options={options}
              value={selectedOption}
              onChange={option => setSelectedKey(option ? option.value : '')}
              formatOptionLabel={formatOptionLabel}
              placeholder={t('administration.reporting.choosePlaceholder')}
              isClearable
            />
          </div>

          <ReportForm
            report={selectedReport}
            timeZone={timeZone}
            isGenerating={Boolean(selectedReport) && pendingKey === selectedReport.key}
            onGenerate={reportParams => generateMutation.mutate({
              reportKey: selectedKey,
              reportParams,
            })}
          />
        </div>

        <ReportHistory
          reports={_.get(reportsQuery, 'data.history', [])}
          formatDate={formatDate}
          onCancel={id => cancelMutation.mutate(id)}
          onDelete={id => deleteMutation.mutate(id)}
        />
      </>
    );
  };

  return (
    <DocumentTitle title={t('administration.pageTitle')}>
      <div>
        <TopNavBar />
        <SubNavBar />
        <AdministrationSubNav />
        <div className='row content'>
          <div className='column'>
            {renderBody()}
          </div>
        </div>
      </div>
    </DocumentTitle>
  );
};

AdministrationReportingPage.propTypes = {
  onErrorMessage: PropTypes.func.isRequired,
};

const mapDispatchToProps = dispatch => ({
  onErrorMessage: message => dispatch(errorMessage(message)),
});

export default connect(null, mapDispatchToProps)(AdministrationReportingPage);
